import { useEffect, useState } from "react";
import {
  Button,
  Card,
  DataState,
  Input,
  PageHeader,
  TextArea,
  useToast,
} from "@vms/ui-web";
import { useApi, useCanIDo } from "../../auth/AuthProvider";
import { useOrgContext } from "../../context/OrgContext";
import { useQuery } from "../../api/queries";

interface OrgProfile {
  id: string;
  name: string;
  legalName?: string | null;
  companyNumber?: string | null;
  taxId?: string | null;
  websiteUrl?: string | null;
  billingCurrency: string;
  billingEmail?: string | null;
  supportEmail?: string | null;
  logoUrl?: string | null;
  notes?: string | null;
  bookingAutoConfirm: boolean;
}

export function OrgSettings() {
  const { currentOrg, refetchOrgs } = useOrgContext();
  const api = useApi();
  const canIDo = useCanIDo();
  const { addToast } = useToast();

  // Fetch full org profile (org list endpoint returns the full row already).
  const orgs = useQuery<OrgProfile[]>("/api/organisations");
  const profile = orgs.data?.find((o) => o.id === currentOrg?.id) ?? null;

  const [form, setForm] = useState<OrgProfile | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (profile && !form) setForm(profile);
  }, [profile, form]);

  if (!currentOrg) return null;
  const canEdit = canIDo("org:settings", currentOrg.id);

  async function save() {
    if (!form) return;
    setSubmitting(true);
    try {
      const res = await api(`/api/organisations/${form.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          name: form.name,
          legalName: form.legalName,
          companyNumber: form.companyNumber,
          taxId: form.taxId,
          websiteUrl: form.websiteUrl,
          billingEmail: form.billingEmail,
          supportEmail: form.supportEmail,
          logoUrl: form.logoUrl,
          notes: form.notes,
          bookingAutoConfirm: form.bookingAutoConfirm,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        addToast(json.error ?? "Failed to save", "error");
        return;
      }
      addToast("Settings saved");
      orgs.refetch();
      refetchOrgs();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Organisation settings" subtitle={currentOrg.name} />

      <DataState loading={orgs.loading || !form} error={orgs.error}>
        {form && (
          <fieldset disabled={!canEdit} className="space-y-6">
            <Card className="p-5 space-y-4">
              <div className="font-semibold text-gray-900">Identity</div>
              <Input
                label="Display name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <Input
                label="Legal name"
                value={form.legalName ?? ""}
                onChange={(e) => setForm({ ...form, legalName: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Company number"
                  value={form.companyNumber ?? ""}
                  onChange={(e) => setForm({ ...form, companyNumber: e.target.value })}
                />
                <Input
                  label="Tax / VAT ID"
                  value={form.taxId ?? ""}
                  onChange={(e) => setForm({ ...form, taxId: e.target.value })}
                />
              </div>
              <Input
                label="Website"
                type="url"
                value={form.websiteUrl ?? ""}
                onChange={(e) => setForm({ ...form, websiteUrl: e.target.value })}
              />
            </Card>

            <Card className="p-5 space-y-4">
              <div className="font-semibold text-gray-900">Contacts</div>
              <Input
                label="Billing email"
                type="email"
                value={form.billingEmail ?? ""}
                onChange={(e) => setForm({ ...form, billingEmail: e.target.value })}
              />
              <Input
                label="Support email"
                type="email"
                value={form.supportEmail ?? ""}
                onChange={(e) => setForm({ ...form, supportEmail: e.target.value })}
              />
              <Input
                label="Logo URL"
                type="url"
                value={form.logoUrl ?? ""}
                onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
              />
              <TextArea
                label="Internal notes"
                value={form.notes ?? ""}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
              />
            </Card>

            <Card className="p-5 space-y-4">
              <div className="font-semibold text-gray-900">Bookings</div>
              <label className="flex items-center gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={form.bookingAutoConfirm}
                  onChange={(e) => setForm({ ...form, bookingAutoConfirm: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <div>
                  <div className="font-medium text-gray-900">Auto-confirm partner bookings</div>
                  <div className="text-xs text-gray-500">
                    When on, bookings created via the partner API are derived to events
                    and pushed to the venue immediately. Override per-venue from the
                    venue settings page.
                  </div>
                </div>
              </label>
            </Card>

            <div className="flex justify-end">
              <Button onClick={save} loading={submitting} disabled={!canEdit}>
                Save changes
              </Button>
            </div>
          </fieldset>
        )}
      </DataState>
    </div>
  );
}
